import { cn } from '@/lib/cn';
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { HTMLAttributes, ReactElement, ReactNode } from 'react';
import { Children, cloneElement, isValidElement } from 'react';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

type HeadingPropsBase = VariantProps<typeof headingVariants> &
  HTMLAttributes<HTMLHeadingElement> & {
    size?: HeadingTag | 'label';
    underlinedText?: string;
    underline?: ReactElement;
    className?: string;
  };

type HeadingProps =
  | (HeadingPropsBase & { asChild?: false; as: HeadingTag })
  | (HeadingPropsBase & { asChild: true; as?: never });

const headingVariants = cva('text-center', {
  variants: {
    size: {
      h1: 'text-[2.125rem] leading-10 lg:text-[2.625rem] lg:leading-13',
      h2: 'text-2xl leading-8.5 lg:text-[2.375rem] lg:leading-11.5',
      h3: 'text-xl leading-8.5 lg:text-3xl lg:leading-10.5',
      h4: 'text-lg leading-8.5 lg:text-[1.375rem] lg:leading-9',
      h5: 'text-lg leading-6',
      label: 'text-sm leading-7.5 uppercase font-medium tracking-[0.18em]',
    },
  },
  defaultVariants: {
    size: 'h1',
  },
});

const processNode = (
  node: ReactNode,
  underlinedText: string,
  underline?: ReactElement,
  found?: { value: boolean },
): ReactNode => {
  if (typeof node === 'string') {
    if (!node.includes(underlinedText)) {
      return node;
    }

    if (found) found.value = true;

    const parts = node.split(underlinedText);
    const result: Array<ReactNode> = [];

    parts.forEach((part, index) => {
      if (part) result.push(part);
      if (index < parts.length - 1 && underline) {
        const underlineProps = underline.props as Record<string, unknown>;
        result.push(
          <span key={`underlined-${index}`} className="relative inline-block">
            {underlinedText}
            {cloneElement(underline, {
              ...underlineProps,
              className: cn(
                'absolute bottom-0 left-0 right-0',
                (underlineProps.className as string | undefined) || '',
              ),
            } as Partial<unknown>)}
          </span>,
        );
      }
    });

    return <>{result}</>;
  }

  if (isValidElement(node)) {
    const element = node as ReactElement<{ children?: ReactNode }>;
    const { children } = element.props;

    if (children) {
      const processedChildren = Children.map(children, (child) =>
        processNode(child, underlinedText, underline, found),
      );

      return cloneElement(element, {
        ...(element.props as Record<string, unknown>),
        children: processedChildren,
      });
    }
  }

  return node;
};

export const Heading = ({
  size,
  as = 'h1',
  asChild,
  underlinedText,
  underline,
  className,
  children,
  ...props
}: HeadingProps) => {
  const Comp = asChild ? Slot : as;
  const finalClassName = cn(headingVariants({ size: size ?? as, className }));

  if (!underlinedText || !underline) {
    return (
      <Comp className={finalClassName} {...props}>
        {children}
      </Comp>
    );
  }

  const found = { value: false };
  const processedChildren = Children.map(children, (child) =>
    processNode(child, underlinedText, underline, found),
  );

  if (!found.value) {
    throw new Error(
      `Heading component: Could not find underlinedText "${underlinedText}" in children`,
    );
  }

  return (
    <Comp className={finalClassName} {...props}>
      {processedChildren}
    </Comp>
  );
};
